import { apiFetch } from './api'
import { formatJalaliDateTime, formatMobile } from './fa'
import { ROLE_LABEL, type StaffRole } from './staff-api'

/** Mirrors `AUDIT_ACTIONS` in apps/api/src/backoffice/audit.actions.ts. */
export const AUDIT_ACTION_LABEL = {
  'staff.login': 'ورود به پیشخوان',
  'staff.logout': 'خروج از پیشخوان',
  'staff.create': 'افزودن همکار',
  'staff.update': 'ویرایش همکار',
  'customer.suspend': 'مسدودکردن مشتری',
  'customer.unsuspend': 'رفع مسدودی مشتری',
  'order.assign': 'سپردن سفارش',
  'order.unassign': 'رهاکردن سفارش',
  'order.reissue': 'صدور دوباره بیمه‌نامه',
} as const
export type AuditAction = keyof typeof AUDIT_ACTION_LABEL

export interface AuditEventDto {
  id: string
  staff: { id: string; name: string } | null
  action: string
  targetType: string
  targetId: string
  before: Record<string, unknown> | null
  after: Record<string, unknown> | null
  ip: string | null
  userAgent: string | null
  createdAt: string
}

export interface AuditPageDto {
  items: AuditEventDto[]
  page: number
  pageSize: number
  total: number
}

export interface AuditFilters {
  staffId?: string
  action?: AuditAction
  /** `YYYY-MM-DD`, read as a whole day in Tehran. */
  from?: string
  to?: string
  page: number
}

/** Iran has had no daylight saving since 2022, so a day there is always +03:30. */
const tehranDay = (day: string, end: boolean) =>
  `${day}T${end ? '23:59:59.999' : '00:00:00.000'}+03:30`

export function listAudit(filters: AuditFilters): Promise<AuditPageDto> {
  const params = new URLSearchParams({ page: String(filters.page) })
  if (filters.staffId) params.set('staffId', filters.staffId)
  if (filters.action) params.set('action', filters.action)
  if (filters.from) params.set('from', tehranDay(filters.from, false))
  if (filters.to) params.set('to', tehranDay(filters.to, true))
  return apiFetch<AuditPageDto>(`/admin/audit?${params.toString()}`)
}

const FIELD_LABEL: Record<string, string> = {
  name: 'نام',
  role: 'نقش',
  isActive: 'وضعیت',
  mobile: 'موبایل',
  suspendedAt: 'مسدود از',
  suspendedReason: 'دلیل مسدودی',
}

const valueLabel = (field: string, value: unknown): string => {
  if (value === null || value === undefined) return '—'
  if (field === 'role') return ROLE_LABEL[value as StaffRole] ?? String(value)
  if (field === 'isActive') return value ? 'فعال' : 'غیرفعال'
  if (field === 'mobile') return formatMobile(String(value))
  if (field === 'suspendedAt') return formatJalaliDateTime(String(value))
  return String(value)
}

/** `نقش: اپراتور ← مدیر` — one line per changed field. */
export function describeChange(event: AuditEventDto): string[] {
  const fields = Object.keys({ ...event.before, ...event.after })
  return fields.map((field) => {
    const label = FIELD_LABEL[field] ?? field
    const now = valueLabel(field, event.after?.[field])
    if (!event.before || !(field in event.before)) return `${label}: ${now}`
    return `${label}: ${valueLabel(field, event.before[field])} ← ${now}`
  })
}
